import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  Paper,
  CircularProgress,
  Snackbar,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { authApi } from "../apis/authApi";
import { usePsaStatus } from "../hooks/usePsaStatus";
import { AutotaskPsaGuideInner } from "./integrationGuides/AutotaskPsaGuideInner";

interface AutotaskSetupFormProps {
  onBack: () => void;
  onSuccess?: () => void;
  /** When provided (e.g. onboarding), called after save so parent can show queue selection before completing */
  onCredentialsSaved?: (platformName: string) => void;
}

const AutotaskSetupForm: React.FC<AutotaskSetupFormProps> = ({ onBack, onSuccess, onCredentialsSaved }) => {
  const navigate = useNavigate();
  const { refetch: refetchPsaStatus } = usePsaStatus();
  const [endpoint, setEndpoint] = useState("https://webservices15.autotask.net/atservicesrest");
  const [apiUsername, setApiUsername] = useState("");
  const [apiPassword, setApiPassword] = useState("");
  const [apiIntegrationCode, setApiIntegrationCode] = useState("");
  const [isTesting, setIsTesting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [testResult, setTestResult] = useState<{
    success: boolean;
    message: string;
  } | null>(null);
  const [showSuccessSnackbar, setShowSuccessSnackbar] = useState(false);
  
  // Track the values when test was successful
  const testedValuesRef = useRef<{
    endpoint: string;
    apiUsername: string;
    apiPassword: string;
    apiIntegrationCode: string;
  } | null>(null);
  
  // Check if fields have changed since successful test
  const hasFieldsChanged = () => {
    if (!testedValuesRef.current || !testResult?.success) {
      return true;
    }
    return (
      endpoint !== testedValuesRef.current.endpoint ||
      apiUsername !== testedValuesRef.current.apiUsername ||
      apiPassword !== testedValuesRef.current.apiPassword ||
      apiIntegrationCode !== testedValuesRef.current.apiIntegrationCode
    );
  };
  
  const isSaveDisabled = !testResult?.success || hasFieldsChanged() || isSaving;

  const handleTestConnection = async () => {
    if (!endpoint || !apiUsername || !apiPassword || !apiIntegrationCode) {
      setTestResult({
        success: false,
        message: "Please fill in all fields before testing the connection.",
      });
      return;
    }

    setIsTesting(true);
    setTestResult(null);

    try {
      const result = await authApi.testAutotaskConnection({
        endpoint,
        apiUsername,
        apiPassword,
        apiIntegrationCode,
      });

      if (result.success) {
        // Store the tested values
        testedValuesRef.current = {
          endpoint,
          apiUsername,
          apiPassword,
          apiIntegrationCode,
        };
      }

      setTestResult({
        success: result.success,
        message: result.message || "Connection test successful!",
      });
    } catch (error: any) {
      // Extract error message from response if available
      const errorMessage = error.response?.data?.error || error.message || "Connection test failed. Please check your credentials.";
      setTestResult({
        success: false,
        message: errorMessage,
      });
      testedValuesRef.current = null;
    } finally {
      setIsTesting(false);
    }
  };

  const handleSave = async () => {
    if (isSaveDisabled) {
      return;
    }

    setIsSaving(true);

    try {
      await authApi.saveAutotaskConfiguration({
        endpoint,
        apiUsername,
        apiPassword,
        apiIntegrationCode,
      });

      // Show success message
      setShowSuccessSnackbar(true);
      
      // Refresh PSA status to update the check
      await refetchPsaStatus();
      
      // Onboarding: let parent show queue selection first, then call onSuccess
      if (onCredentialsSaved) {
        setTimeout(() => {
          onCredentialsSaved("autotask");
        }, 800);
        return;
      }
      if (onSuccess) {
        setTimeout(() => {
          onSuccess();
        }, 1500);
        return;
      }
      // Standalone: redirect to the main analytics dashboard
      setTimeout(() => {
        window.location.href = "/msp-analytics";
      }, 1500);
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || error.message || "Failed to save Autotask configuration.";
      setTestResult({
        success: false,
        message: errorMessage,
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Box>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={onBack}
        sx={{
          fontFamily: "var(--theo-font-sans)",
          color: "#6c6c6c",
          textTransform: "none",
          mb: 3,
          "&:hover": {
            background: "#f5f5f5",
          },
        }}
      >
        Back to PSA Selection
      </Button>

      <Typography
        variant="h5"
        component="h2"
        sx={{
          fontFamily: "var(--theo-font-sans)",
          fontWeight: 700,
          mb: 3,
          color: "#1a1a1a",
        }}
      >
        Autotask Setup
      </Typography>

      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 4,
          background: "#f8fafc",
          borderRadius: 2,
          border: "1px solid #e5e5e5",
        }}
      >
        <Box sx={{ color: "#6c6c6c", "& .MuiTypography-root": { color: "inherit" } }}>
          <AutotaskPsaGuideInner showGuideTitle={false} />
        </Box>
      </Paper>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        <TextField
          label="Endpoint"
          value={endpoint}
          onChange={(e) => {
            setEndpoint(e.target.value);
            // Clear test result if fields change after successful test
            if (testResult?.success) {
              setTestResult(null);
            }
          }}
          placeholder="https://webservices2.autotask.net"
          fullWidth
          required
          sx={{
            fontFamily: "var(--theo-font-sans)",
            "& .MuiInputLabel-root": {
              fontFamily: "var(--theo-font-sans)",
            },
            "& .MuiInputBase-input": {
              fontFamily: "var(--theo-font-sans)",
            },
          }}
          helperText="Your Autotask API endpoint URL"
        />

        <TextField
          label="API Username"
          value={apiUsername}
          onChange={(e) => {
            setApiUsername(e.target.value);
            if (testResult?.success) {
              setTestResult(null);
            }
          }}
          placeholder="ebt3zvcty@MyAutotaskDomain.com"
          fullWidth
          required
          sx={{
            fontFamily: "var(--theo-font-sans)",
            "& .MuiInputLabel-root": {
              fontFamily: "var(--theo-font-sans)",
            },
            "& .MuiInputBase-input": {
              fontFamily: "var(--theo-font-sans)",
            },
          }}
        />

        <TextField
          label="API Password"
          type="password"
          value={apiPassword}
          onChange={(e) => {
            setApiPassword(e.target.value);
            if (testResult?.success) {
              setTestResult(null);
            }
          }}
          fullWidth
          required
          sx={{
            fontFamily: "var(--theo-font-sans)",
            "& .MuiInputLabel-root": {
              fontFamily: "var(--theo-font-sans)",
            },
            "& .MuiInputBase-input": {
              fontFamily: "var(--theo-font-sans)",
            },
          }}
        />

        <TextField
          label="API Integration Code"
          value={apiIntegrationCode}
          onChange={(e) => {
            setApiIntegrationCode(e.target.value);
            if (testResult?.success) {
              setTestResult(null);
            }
          }}
          placeholder="CIZPHWT4CKJGIIWQ3FPQGFQNC3G"
          fullWidth
          required
          sx={{
            fontFamily: "var(--theo-font-sans)",
            "& .MuiInputLabel-root": {
              fontFamily: "var(--theo-font-sans)",
            },
            "& .MuiInputBase-input": {
              fontFamily: "var(--theo-font-sans)",
            },
          }}
        />

        {testResult && (
          <Alert
            severity={testResult.success ? "success" : "error"}
            sx={{
              fontFamily: "var(--theo-font-sans)",
              "& .MuiAlert-message": {
                fontFamily: "var(--theo-font-sans)",
              },
            }}
          >
            {testResult.message}
          </Alert>
        )}

        <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
          <Button
            variant="outlined"
            onClick={handleTestConnection}
            disabled={isTesting || !endpoint || !apiUsername || !apiPassword || !apiIntegrationCode}
            sx={{
              fontFamily: "var(--theo-font-sans)",
              textTransform: "none",
              fontWeight: 500,
              borderColor: "#705d4a",
              color: "#705d4a",
              "&:hover": {
                borderColor: "#604d3a",
                background: "#f5f5f5",
              },
              minWidth: 150,
            }}
          >
            {isTesting ? (
              <>
                <CircularProgress size={16} sx={{ mr: 1 }} />
                Testing...
              </>
            ) : (
              "Test Connection"
            )}
          </Button>

          <Button
            variant="contained"
            onClick={handleSave}
            disabled={isSaveDisabled}
            sx={{
              fontFamily: "var(--theo-font-sans)",
              textTransform: "none",
              fontWeight: 500,
              background: isSaveDisabled ? "#e0e0e0" : "#705d4a",
              color: isSaveDisabled ? "#9e9e9e" : "#fff",
              minWidth: 150,
              "&:hover": {
                background: isSaveDisabled ? "#e0e0e0" : "#604d3a",
              },
            }}
          >
            {isSaving ? (
              <>
                <CircularProgress size={16} sx={{ mr: 1 }} />
                Saving...
              </>
            ) : (
              "Save"
            )}
          </Button>
        </Box>
      </Box>

      <Snackbar
        open={showSuccessSnackbar}
        autoHideDuration={3000}
        onClose={() => setShowSuccessSnackbar(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setShowSuccessSnackbar(false)}
          severity="success"
          sx={{ fontFamily: "var(--theo-font-sans)" }}
        >
          PSA has been successfully connected! Redirecting to dashboard...
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AutotaskSetupForm;

